import React, { useState, useEffect } from 'react';
import CollapsibleSection from '../../CollapsibleSection';
import { Patient, PatientAppointment, StaffMeeting } from '../../../types';
import { FirebasePatientService, Patient as ServicePatient } from '../../../services/firebase/FirebasePatientService';
import { firebaseAppointmentService } from '../../../services/firebase/FirebaseAppointmentService';
import { Appointment } from '../../../services/firebase/FirebaseAppointmentService';
import FirebaseStaffMeetingService, { StaffMeetingData } from '../../../services/firebase/FirebaseStaffMeetingService';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { UserRole } from '../../../types/roles';
import LegalsCard from './LegalsCard';
import SessionTimerCard from './SessionTimerCard';

// Helper: convert UTC midnight Firestore dates to correct local date
const toLocalDate = (d: Date): Date =>
  new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

const formatTime12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

interface OverviewTabProps {
  patients: Patient[];
  patientAppointments: PatientAppointment[];
  staffMeetings: StaffMeeting[];
  sessionActive: boolean;
  onTabChange: (tab: 'overview' | 'schedule' | 'meetings' | 'prescriptions' | 'sessions' | 'clients') => void;
  roles?: UserRole[];
  practiceOwnerId?: string;
}

interface AppointmentRequest {
  id: string;
  patientName: string;
  requestedDate: string;
  requestedTime: string;
  requestType: 'new_patient' | 'existing_patient' | 'follow_up';
  reason: string;
  timestamp: Date;
  status: 'pending' | 'accepted' | 'declined';
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  patients,
  patientAppointments,
  staffMeetings,
  sessionActive,
  onTabChange,
  roles = [],
  practiceOwnerId,
}) => {
  const isAdminOrDirector = roles.includes('administrative_staff') || roles.includes('clinical_director') || roles.includes('system_admin');
  const [newPatients, setNewPatients] = useState<ServicePatient[]>([]);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [appointmentRequests, setAppointmentRequests] = useState<AppointmentRequest[]>([]);
  const [requestsExpanded, setRequestsExpanded] = useState(() => {
    const saved = localStorage.getItem('requestsExpanded');
    return saved ? JSON.parse(saved) : appointmentRequests.filter(r => r.status === 'pending').length <= 5;
  });
  const [requestsView, setRequestsView] = useState<'pending' | 'archived'>('pending');
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AppointmentRequest | null>(null);
  const [rescheduleData, setRescheduleData] = useState({
    date: '',
    time: ''
  });
  const [realAppointments, setRealAppointments] = useState<Appointment[]>([]);
  const [practicePatientCount, setPracticePatientCount] = useState(0);
  const [incompleteFormCount, setIncompleteFormCount] = useState(0);
  const [realStaffMeetings, setRealStaffMeetings] = useState<StaffMeetingData[]>([]);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unsignedNoteCount, setUnsignedNoteCount] = useState(0);
  const [upcomingWeekAppointments, setUpcomingWeekAppointments] = useState<Appointment[]>([]);
  const [recentMessages, setRecentMessages] = useState<Array<{ senderName: string; text: string; timestamp: any }>>([]);
  const { user } = useAuth();
  const providerId = user?.uid || '';

  // Load appointments from Firestore — practice-wide for admin/director, provider-specific for clinicians
  useEffect(() => {
    if (!providerId) return;
    const filters = isAdminOrDirector ? {} : { providerId };
    const unsub = firebaseAppointmentService.subscribeToAppointments(
      filters,
      (appts) => setRealAppointments(appts)
    );
    return () => unsub();
  }, [providerId, isAdminOrDirector]);

  // Load patients — practice-wide for admin/director
  useEffect(() => {
    if (!providerId) return;
    if (isAdminOrDirector) {
      // Practice-wide patient count
      FirebasePatientService.getAllPracticePatients().then(all => {
        setPracticePatientCount(all.length);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const recent = all.filter(p => p.createdAt >= sevenDaysAgo);
        setNewPatients(recent as any[]);
        setTotalDocuments(all.length);
      }).catch(() => {});
    } else {
      const unsubscribe = FirebasePatientService.subscribeToPatients(providerId, (allPatients) => {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const recent = allPatients.filter(p => p.createdAt >= sevenDaysAgo);
        setNewPatients(recent as any[]);
        setTotalDocuments(allPatients.length);
      });
      return () => unsubscribe();
    }
  }, [providerId, isAdminOrDirector]);

  // Load staff meetings for admin/director
  useEffect(() => {
    if (!isAdminOrDirector || !practiceOwnerId) return;
    const service = FirebaseStaffMeetingService.getInstance();
    const unsub = service.subscribeToMeetings(practiceOwnerId, (meetings) => {
      setRealStaffMeetings(meetings);
    });
    return () => unsub();
  }, [isAdminOrDirector, practiceOwnerId]);

  // Load incomplete form assignments for admin/director
  useEffect(() => {
    if (!isAdminOrDirector) return;
    const loadIncompleteForms = async () => {
      try {
        const q = query(
          collection(db, 'form_assignments'),
          where('status', '==', 'pending')
        );
        const snap = await getDocs(q);
        setIncompleteFormCount(snap.size);
      } catch (err) {
        console.warn('Could not load incomplete forms:', err);
      }
    };
    loadIncompleteForms();
  }, [isAdminOrDirector]);

  // Load unread messages count + recent messages
  useEffect(() => {
    if (!providerId) return;
    const loadMessages = async () => {
      try {
        // Find team conversations this provider is part of
        let totalUnread = 0;
        try {
          const convQ = query(
            collection(db, 'team_conversations'),
            where('members', 'array-contains', providerId)
          );
          const convSnap = await getDocs(convQ);
          convSnap.docs.forEach(d => {
            const data = d.data();
            const unread = data.unreadCount?.[providerId] || 0;
            totalUnread += unread;
          });
        } catch {
          // Team conversations may not exist yet — continue with patient messages
        }

        // Also check patient conversations
        const patientConvQ = query(
          collection(db, 'conversations'),
          where('providerId', '==', providerId)
        );
        const patientConvSnap = await getDocs(patientConvQ);
        const latestMsgs: Array<{ senderName: string; text: string; timestamp: any }> = [];
        for (const convDoc of patientConvSnap.docs) {
          const convData = convDoc.data();
          const unread = convData.unreadCount?.[providerId] || 0;
          totalUnread += unread;
          if (convData.lastMessage) {
            const msgText = typeof convData.lastMessage === 'string'
              ? convData.lastMessage
              : convData.lastMessage?.content || '';
            latestMsgs.push({
              senderName: convData.clientName || 'Client',
              text: msgText,
              timestamp: convData.lastMessageAt || convData.lastMessage?.timestamp
            });
          }
        }
        // Sort by most recent and take top 3
        latestMsgs.sort((a, b) => {
          const ta = a.timestamp?.seconds || 0;
          const tb = b.timestamp?.seconds || 0;
          return tb - ta;
        });
        setRecentMessages(latestMsgs.slice(0, 3));
        setUnreadMessageCount(totalUnread);
      } catch (err) {
        console.warn('Could not load unread messages:', err);
      }
    };
    loadMessages();
  }, [providerId]);

  // Load unsigned/draft clinical notes count
  useEffect(() => {
    if (!providerId) return;
    const loadUnsignedNotes = async () => {
      try {
        const notesQ = query(
          collection(db, 'clinical_notes'),
          where('providerId', '==', providerId),
          where('status', '==', 'draft')
        );
        const snap = await getDocs(notesQ);
        setUnsignedNoteCount(snap.size);
      } catch (err) {
        // Try without status filter in case field doesn't exist on all docs
        console.warn('Could not load unsigned notes:', err);
      }
    };
    loadUnsignedNotes();
  }, [providerId]);

  // Load upcoming week appointments (next 7 days, excluding today which is shown separately)
  useEffect(() => {
    if (!providerId) return;
    const loadUpcoming = async () => {
      try {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const weekOut = new Date();
        weekOut.setDate(weekOut.getDate() + 7);
        weekOut.setHours(23, 59, 59, 999);

        const filters = isAdminOrDirector ? {} : { providerId };
        const allAppts = await firebaseAppointmentService.getAppointments(filters);
        const upcoming = allAppts.filter(a => {
          const d = toLocalDate(new Date(a.appointmentDate));
          return d >= tomorrow && d <= weekOut && a.status !== 'cancelled' && a.status !== 'no-show';
        }).sort((a, b) => new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime() || a.startTime.localeCompare(b.startTime));
        setUpcomingWeekAppointments(upcoming);
      } catch (err) {
        console.warn('Could not load upcoming appointments:', err);
      }
    };
    loadUpcoming();
  }, [providerId, isAdminOrDirector]);

  useEffect(() => {
    localStorage.setItem('requestsExpanded', JSON.stringify(requestsExpanded));
  }, [requestsExpanded]);

  const handleAcceptRequest = (requestId: string) => {
    const request = appointmentRequests.find(r => r.id === requestId);
    if (!request) return;

    // Mark as accepted
    setAppointmentRequests(prev =>
      prev.map(req => req.id === requestId ? { ...req, status: 'accepted' as const } : req)
    );

    // Switch to archived view to show the result
    setTimeout(() => {
      setRequestsView('archived');
      // Navigate to schedule tab after a brief delay
      setTimeout(() => {
        onTabChange('schedule');
      }, 500);
    }, 500);
  };

  const handleDeclineRequest = (requestId: string) => {
    setAppointmentRequests(prev =>
      prev.map(req => req.id === requestId ? { ...req, status: 'declined' as const } : req)
    );
    // Switch to archived view to show the result
    setTimeout(() => setRequestsView('archived'), 500);
  };

  const handleRescheduleRequest = (requestId: string) => {
    const request = appointmentRequests.find(r => r.id === requestId);
    if (!request) return;

    setSelectedRequest(request);
    setRescheduleData({
      date: request.requestedDate,
      time: request.requestedTime
    });
    setShowRescheduleModal(true);
  };

  const handleViewRequestDetails = (requestId: string) => {
    const request = appointmentRequests.find(r => r.id === requestId);
    if (!request) return;

    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleSaveReschedule = () => {
    if (!selectedRequest) return;

    setAppointmentRequests(prev =>
      prev.map(req =>
        req.id === selectedRequest.id
          ? { ...req, requestedDate: rescheduleData.date, requestedTime: rescheduleData.time }
          : req
      )
    );

    setShowRescheduleModal(false);
    setSelectedRequest(null);
  };

  const activePatients = patients.filter(p => p.status === 'active');
  const upcomingStaffMeetings = staffMeetings.filter(meeting => meeting.status === 'upcoming');
  // Real staff meetings: upcoming = scheduled status and date >= today
  const upcomingRealMeetings = realStaffMeetings.filter(m => m.status === 'scheduled' && m.date >= new Date().toISOString().split('T')[0]);

  // Real Firestore appointment stats
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todaysRealAppointments = realAppointments.filter(appt => {
    const apptDate = toLocalDate(new Date(appt.appointmentDate));
    return apptDate >= today && apptDate < tomorrow && appt.status !== 'cancelled';
  }).sort((a, b) => a.startTime.localeCompare(b.startTime));

  // Legacy: keep for backward compat with mock data rendering
  const todaysPatientAppointments = patientAppointments.filter(apt => apt.date === '2024-09-09');
  const upcomingPatientAppointments = todaysPatientAppointments.filter(apt => apt.status === 'upcoming');

  return (
    <div>
      {/* Legals: forms and staff documents that are missing, overdue or due for renewal */}
      {isAdminOrDirector && (
        <div style={{ marginBottom: '1.5rem' }}>
          <LegalsCard practiceOwnerId={practiceOwnerId} />
        </div>
      )}

      {/* Phone / home session timer */}
      <div style={{ marginBottom: '1.5rem' }}>
        <SessionTimerCard patients={patients} practiceOwnerId={practiceOwnerId} />
      </div>

      {/* Quick Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div className="card" style={{ padding: '0.75rem 1rem' }}>
          <div>
            <h3 style={{ margin: 0, color: '#D4A017', fontSize: '1.25rem' }}>
              {isAdminOrDirector ? practicePatientCount || totalDocuments : (totalDocuments || activePatients.length)}
            </h3>
            <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.8rem' }}>
              {isAdminOrDirector ? 'Total Practice Patients' : 'Active Patients'}
            </p>
          </div>
        </div>

        <div className="card" style={{ padding: '0.75rem 1rem' }}>
          <div>
            <h3 style={{ margin: 0, color: '#D4A017', fontSize: '1.25rem' }}>
              {todaysRealAppointments.length}
            </h3>
            <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.8rem' }}>
              {isAdminOrDirector ? 'Practice Appointments Today' : 'Patient Appointments Today'}
            </p>
          </div>
        </div>

        <div className="card" style={{ padding: '0.75rem 1rem', cursor: 'pointer' }} onClick={() => onTabChange('meetings')}>
          <div>
            <h3 style={{ margin: 0, color: '#3b82f6', fontSize: '1.25rem' }}>
              {isAdminOrDirector ? upcomingRealMeetings.length : upcomingStaffMeetings.length}
            </h3>
            <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.8rem' }}>
              Upcoming Staff Meetings
            </p>
          </div>
        </div>

        {isAdminOrDirector ? (
          <div className="card" style={{ padding: '0.75rem 1rem' }}>
            <div>
              <h3 style={{ margin: 0, color: incompleteFormCount > 0 ? '#f59e0b' : '#10b981', fontSize: '1.25rem' }}>
                {incompleteFormCount}
              </h3>
              <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.8rem' }}>
                Incomplete Client Forms
              </p>
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: '0.75rem 1rem' }}>
            <div>
              <h3 style={{ margin: 0, color: sessionActive ? '#10b981' : '#6b7280', fontSize: '1.25rem' }}>
                {sessionActive ? 'Active' : 'Inactive'}
              </h3>
              <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.8rem' }}>
                Session Status
              </p>
            </div>
          </div>
        )}

        {isAdminOrDirector ? (
          <div className="card" style={{ padding: '0.75rem 1rem' }}>
            <div>
              <h3 style={{ margin: 0, color: sessionActive ? '#10b981' : '#6b7280', fontSize: '1.25rem' }}>
                {sessionActive ? 'Active' : 'None'}
              </h3>
              <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.8rem' }}>
                Active Provider Sessions
              </p>
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: '0.75rem 1rem' }}>
            <div>
              <h3 style={{ margin: 0, color: '#10b981', fontSize: '1.25rem' }}>
                {totalDocuments}
              </h3>
              <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.8rem' }}>
                Completed Forms
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Action Items — Unread Messages + Unsigned Notes */}
      {(unreadMessageCount > 0 || unsignedNoteCount > 0) && (
        <CollapsibleSection
          title={`Action Items (${unreadMessageCount + unsignedNoteCount})`}
          variant="warning"
          defaultExpanded={true}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {unreadMessageCount > 0 && (
              <div style={{
                padding: '1rem',
                background: '#16325B',
                borderRadius: '0.5rem',
                borderLeft: '4px solid #3b82f6',
                cursor: 'pointer'
              }}
              onClick={() => onTabChange('schedule')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '1rem' }}>
                    Unread Messages
                  </h4>
                  <span style={{
                    background: '#3b82f6',
                    color: 'white',
                    padding: '2px 10px',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    {unreadMessageCount}
                  </span>
                </div>
                {recentMessages.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {recentMessages.map((msg, i) => (
                      <p key={i} style={{ margin: 0, color: '#d1d5db', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <strong style={{ color: '#D4A017' }}>{msg.senderName}:</strong> {msg.text}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {unsignedNoteCount > 0 && (
              <div style={{
                padding: '1rem',
                background: '#16325B',
                borderRadius: '0.5rem',
                borderLeft: '4px solid #f59e0b',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '1rem' }}>
                    Unsigned Notes
                  </h4>
                  <span style={{
                    background: '#f59e0b',
                    color: '#000',
                    padding: '2px 10px',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    {unsignedNoteCount}
                  </span>
                </div>
                <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.8rem' }}>
                  Draft clinical notes awaiting review and signature
                </p>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* New Patient Notifications */}
      {newPatients.length > 0 && (
        <CollapsibleSection
          title={`New Patient Registrations (${newPatients.length})`}
          variant="warning"
          defaultExpanded={true}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem'
          }}>
            <p style={{ color: '#D4A017', textAlign: 'center', padding: '1rem' }}>
              {newPatients.length} new patient{newPatients.length !== 1 ? 's' : ''} registered in the last 7 days
            </p>
          </div>
        </CollapsibleSection>
      )}

      {/* Today's Schedule Preview */}
      <CollapsibleSection
        title="Today's Schedule Preview"
        variant="success"
        defaultExpanded={false}
      >
        <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
          <span style={{ fontSize: '0.875rem', color: '#d1d5db' }}>
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </span>
        </div>

        {/* Real Firestore Appointments Today */}
        {todaysRealAppointments.length > 0 ? (
          todaysRealAppointments.map((appt) => (
            <div key={`real-${appt.id}`} style={{
              padding: '1rem',
              background: '#16325B',
              borderRadius: '0.5rem',
              marginBottom: '0.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderLeft: `4px solid ${appt.modality === 'video' ? '#3b82f6' : '#10b981'}`,
              cursor: 'pointer'
            }}
            onClick={() => onTabChange('schedule')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  background: '#D4A017',
                  color: 'white',
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  minWidth: '80px',
                  textAlign: 'center'
                }}>
                  {formatTime12Hour(appt.startTime)}
                </div>
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', color: '#f9fafb' }}>
                    {appt.patientName || 'Patient'}
                  </h4>
                  <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.875rem' }}>
                    {appt.sessionType} • {appt.modality === 'video' ? '📹 Video' : '👤 In-Person'}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p style={{ color: '#d1d5db', textAlign: 'center', margin: '1rem 0' }}>No appointments scheduled for today</p>
        )}

        {/* Staff Meetings Today */}
        {staffMeetings.filter(meeting => meeting.date === '2024-09-09').map((meeting) => (
          <div key={`staff-${meeting.id}`} style={{
            padding: '1rem',
            background: meeting.status === 'completed' ? '#16325B' : '#16325B',
            borderRadius: '0.5rem',
            marginBottom: '0.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            opacity: meeting.status === 'completed' ? 0.7 : 1
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{
                background: meeting.status === 'completed' ? '#6b7280' : '#3b82f6',
                color: 'white',
                padding: '0.5rem',
                borderRadius: '0.5rem',
                fontSize: '0.75rem',
                fontWeight: '600',
                minWidth: '70px',
                textAlign: 'center'
              }}>
                {meeting.time}
              </div>
              <div>
                <h4 style={{ margin: '0 0 0.25rem 0', color: '#f9fafb' }}>
                  {meeting.title}
                </h4>
                <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.875rem' }}>
                  {meeting.agenda} • Staff Meeting
                </p>
              </div>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* Upcoming This Week */}
      {upcomingWeekAppointments.length > 0 && (
        <CollapsibleSection
          title={`Upcoming This Week (${upcomingWeekAppointments.length})`}
          variant="default"
          defaultExpanded={false}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {upcomingWeekAppointments.map((appt) => {
              const apptDate = toLocalDate(new Date(appt.appointmentDate));
              const dayLabel = apptDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              return (
                <div key={`upcoming-${appt.id}`} style={{
                  padding: '0.75rem 1rem',
                  background: '#16325B',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderLeft: `4px solid ${appt.modality === 'video' ? '#3b82f6' : '#10b981'}`,
                  cursor: 'pointer'
                }}
                onClick={() => onTabChange('schedule')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      background: '#28527A',
                      color: '#D4A017',
                      padding: '0.4rem 0.6rem',
                      borderRadius: '0.5rem',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      minWidth: '90px',
                      textAlign: 'center'
                    }}>
                      {dayLabel}
                    </div>
                    <div style={{
                      background: '#D4A017',
                      color: 'white',
                      padding: '0.3rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      minWidth: '65px',
                      textAlign: 'center'
                    }}>
                      {formatTime12Hour(appt.startTime)}
                    </div>
                    <div>
                      <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '0.9rem' }}>
                        {appt.patientName || 'Patient'}
                      </h4>
                      <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.8rem' }}>
                        {appt.sessionType} • {appt.modality === 'video' ? 'Video' : 'In-Person'}{appt.providerName ? ` • ${appt.providerName}` : ''}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* Appointment Requests */}
      {appointmentRequests.length > 0 && (
        <CollapsibleSection
          title="Appointment Requests"
          variant="warning"
          defaultExpanded={requestsExpanded}
        >
          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            marginBottom: '1rem',
            borderBottom: '2px solid #28527A',
            paddingBottom: '0.5rem'
          }}>
            <button
              onClick={() => setRequestsView('pending')}
              style={{
                background: 'transparent',
                border: 'none',
                color: requestsView === 'pending' ? '#D4A017' : '#9ca3af',
                fontSize: '0.95rem',
                fontWeight: requestsView === 'pending' ? '600' : '400',
                cursor: 'pointer',
                padding: '0.25rem 0',
                borderBottom: requestsView === 'pending' ? '3px solid #D4A017' : 'none',
                marginBottom: requestsView === 'pending' ? '-2px' : '0',
                position: 'relative'
              }}
            >
              Pending requests ({appointmentRequests.filter(r => r.status === 'pending').length})
            </button>
            <button
              onClick={() => setRequestsView('archived')}
              style={{
                background: 'transparent',
                border: 'none',
                color: requestsView === 'archived' ? '#D4A017' : '#9ca3af',
                fontSize: '0.95rem',
                fontWeight: requestsView === 'archived' ? '600' : '400',
                cursor: 'pointer',
                padding: '0.25rem 0',
                borderBottom: requestsView === 'archived' ? '3px solid #D4A017' : 'none',
                marginBottom: requestsView === 'archived' ? '-2px' : '0',
                position: 'relative'
              }}
            >
              Archived requests ({appointmentRequests.filter(r => r.status !== 'pending').length})
            </button>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem'
          }}>
            {appointmentRequests
              .filter(r => requestsView === 'pending' ? r.status === 'pending' : r.status !== 'pending')
              .slice(0, requestsExpanded ? undefined : 5)
              .map((request) => (
                <div
                  key={request.id}
                  style={{
                    padding: '0.75rem 1rem',
                    background: '#16325B',
                    borderRadius: '0.5rem',
                    border: '1px solid #28527A',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem'
                  }}
                >
                  {/* Left side - Request info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{ margin: '0 0 0.25rem 0', color: '#f9fafb', fontSize: '0.95rem' }}>
                      {request.patientName}
                    </h4>
                    <p style={{ margin: '0 0 0.5rem 0', color: '#d1d5db', fontSize: '0.8rem' }}>
                      {request.reason}
                    </p>
                    <div style={{
                      display: 'flex',
                      gap: '0.75rem',
                      fontSize: '0.8rem',
                      color: '#D4A017',
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      <span>{new Date(request.requestedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span>•</span>
                      <span>{request.requestedTime}</span>
                      <span>•</span>
                      <span style={{
                        padding: '2px 8px',
                        background: request.requestType === 'new_patient' ? '#3b82f6' : '#6b7280',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        color: 'white',
                        whiteSpace: 'nowrap'
                      }}>
                        {request.requestType === 'new_patient' ? 'New Patient' :
                         request.requestType === 'existing_patient' ? 'Existing' : 'Follow-up'}
                      </span>
                    </div>
                  </div>

                  {/* Right side - Action buttons or Status badge */}
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    flexShrink: 0,
                    alignItems: 'center'
                  }}>
                    {requestsView === 'pending' ? (
                      <>
                        <button
                          onClick={() => handleAcceptRequest(request.id)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => handleDeclineRequest(request.id)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Decline
                        </button>
                        <button
                          onClick={() => handleRescheduleRequest(request.id)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Reschedule
                        </button>
                        <button
                          onClick={() => handleViewRequestDetails(request.id)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: '#28527A',
                            color: '#e5e7eb',
                            border: '1px solid #6CA6C1',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          View Details
                        </button>
                      </>
                    ) : (
                      <span style={{
                        padding: '0.5rem 1rem',
                        background: request.status === 'accepted' ? '#10b981' : '#6b7280',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}>
                        {request.status === 'accepted' ? 'Accepted' : 'Declined'}
                      </span>
                    )}
                  </div>
                </div>
              ))}

            {appointmentRequests.filter(r => requestsView === 'pending' ? r.status === 'pending' : r.status !== 'pending').length > 5 && (
              <button
                onClick={() => setRequestsExpanded(!requestsExpanded)}
                style={{
                  padding: '0.75rem',
                  background: 'transparent',
                  color: '#D4A017',
                  border: '1px solid #D4A017',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: '500',
                  textAlign: 'center'
                }}
              >
                {requestsExpanded
                  ? 'Show Less'
                  : `View All ${appointmentRequests.filter(r => requestsView === 'pending' ? r.status === 'pending' : r.status !== 'pending').length} Requests`
                }
              </button>
            )}

            {/* Empty state for archived when no archived requests */}
            {requestsView === 'archived' && appointmentRequests.filter(r => r.status !== 'pending').length === 0 && (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#d1d5db'
              }}>
                <p>No archived requests yet.</p>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Quick Actions */}
      <CollapsibleSection
        title="Quick Actions"
        variant="default"
        defaultExpanded={false}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          {!isAdminOrDirector && (
            <div style={{
              padding: '1rem',
              background: '#28527A',
              borderRadius: '8px',
              border: '1px solid #6CA6C1',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '1rem' }}>Start Session</h4>
              <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.875rem', flex: 1 }}>
                Begin a new patient session
              </p>
              <button
                className="btn btn-primary"
                onClick={() => onTabChange('sessions')}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  width: 'fit-content'
                }}
              >
                View
              </button>
            </div>
          )}

          <div style={{
            padding: '1rem',
            background: '#28527A',
            borderRadius: '8px',
            border: '1px solid #6CA6C1',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
            <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '1rem' }}>Appointments</h4>
            <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.875rem', flex: 1 }}>
              {isAdminOrDirector ? 'View practice schedule' : 'Manage your schedule'}
            </p>
            <button
              className="btn btn-secondary"
              onClick={() => onTabChange('schedule')}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                width: 'fit-content'
              }}
            >
              View
            </button>
          </div>

          <div style={{
            padding: '1rem',
            background: '#28527A',
            borderRadius: '8px',
            border: '1px solid #6CA6C1',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
            <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '1rem' }}>Staff Meetings</h4>
            <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.875rem', flex: 1 }}>
              View and manage meetings
            </p>
            <button
              className="btn btn-secondary"
              onClick={() => onTabChange('meetings')}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                width: 'fit-content'
              }}
            >
              View
            </button>
          </div>

          {isAdminOrDirector && (
            <div style={{
              padding: '1rem',
              background: '#28527A',
              borderRadius: '8px',
              border: '1px solid #6CA6C1',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <h4 style={{ margin: 0, color: '#f9fafb', fontSize: '1rem' }}>Clients</h4>
              <p style={{ margin: 0, color: '#d1d5db', fontSize: '0.875rem', flex: 1 }}>
                Manage client records and files
              </p>
              <button
                className="btn btn-secondary"
                onClick={() => onTabChange('clients')}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  width: 'fit-content'
                }}
              >
                View
              </button>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            border: '2px solid #6CA6C1',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              color: '#f9fafb',
              fontSize: '20px'
            }}>
              Reschedule Appointment Request
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Patient</div>
              <div style={{ color: '#f9fafb', fontSize: '16px', fontWeight: 'bold' }}>
                {selectedRequest.patientName}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
                New Date
              </label>
              <input
                type="date"
                value={rescheduleData.date}
                onChange={(e) => setRescheduleData({ ...rescheduleData, date: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: '1px solid #4b5563',
                  borderRadius: '6px'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
                New Time
              </label>
              <input
                type="time"
                value={rescheduleData.time}
                onChange={(e) => setRescheduleData({ ...rescheduleData, time: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: '1px solid #4b5563',
                  borderRadius: '6px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSaveReschedule}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setShowRescheduleModal(false);
                  setSelectedRequest(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            border: '2px solid #6CA6C1',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              color: '#f9fafb',
              fontSize: '20px'
            }}>
              Appointment Request Details
            </h3>

            <div style={{
              backgroundColor: '#374151',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Patient Name</div>
                <div style={{ color: '#f9fafb', fontSize: '16px', fontWeight: 'bold' }}>
                  {selectedRequest.patientName}
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Request Type</div>
                <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                  <span style={{
                    padding: '4px 12px',
                    background: selectedRequest.requestType === 'new_patient' ? '#3b82f6' : '#6b7280',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: 'white'
                  }}>
                    {selectedRequest.requestType === 'new_patient' ? 'New Patient' :
                     selectedRequest.requestType === 'existing_patient' ? 'Existing Patient' : 'Follow-up'}
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Requested Date & Time</div>
                <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                  {new Date(selectedRequest.requestedDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })} at {selectedRequest.requestedTime}
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Reason for Visit</div>
                <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                  {selectedRequest.reason}
                </div>
              </div>

              <div>
                <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Request Submitted</div>
                <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                  {selectedRequest.timestamp.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })} at {selectedRequest.timestamp.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              {selectedRequest.status === 'pending' && (
                <>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleAcceptRequest(selectedRequest.id);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Accept Request
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleRescheduleRequest(selectedRequest.id);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Reschedule
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleDeclineRequest(selectedRequest.id);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Decline
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedRequest(null);
                }}
                style={{
                  flex: selectedRequest.status === 'pending' ? 0 : 1,
                  padding: '12px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;